export interface ConcernMapping {
  concernText: string;
  reasonText: string;
  expectedOutcome: string;
}

// Lookup key is treatmentConcerns array joined with ','
// e.g. single: 'ACNE_OR_PIMPLES', combined: 'UNDER_EYE_DARK_CIRCLES,PIGMENTATION'
export const CONCERN_MAPPINGS: { [key: string]: ConcernMapping } = {
  // Face — single concerns
  'ACNE_OR_PIMPLES': {
    concernText: 'acne breakouts',
    reasonText: 'excess oil, clogged pores, or bacterial build-up',
    expectedOutcome: 'calm acne and support clearer skin.',
  },
  'ACNE_SCARS': {
    concernText: 'acne scarring',
    reasonText: 'past acne and picking, or squeezing pimples',
    expectedOutcome: 'improve skin texture and reduce scars.',
  },
  'ANTI_AGING': {
    concernText: 'signs of wrinkles and loss of firmness',
    reasonText: 'slow cell turnover, or collagen loss',
    expectedOutcome: 'improve elasticity and skin renewal to support a youthful look.',
  },
  'COMEDONES': {
    concernText: 'small bumps',
    reasonText: 'clogged pores, or excess oil',
    expectedOutcome: 'clear pores, reduce bumps and improve overall skin texture.',
  },
  'UNDER_EYE_DARK_CIRCLES': {
    concernText: 'darkening around the area',
    reasonText: 'fatigue, or reduced blood circulation',
    expectedOutcome: 'brighten the dark circles, giving you fresher, even-toned skin.',
  },
  'DARK_SPOTS_OR_MARK': {
    concernText: 'dark spots',
    reasonText: 'excess melanin production, sun exposure, or past acne',
    expectedOutcome: 'fade the spots and prevent further scarring — for clear, even-toned skin.',
  },
  'DULL_SKIN': {
    concernText: 'dullness',
    reasonText: 'dehydration, slow cell turnover, or dead skin cell buildup',
    expectedOutcome: 'revive radiance and glow by focusing on exfoliation and hydration.',
  },
  'PIGMENTATION': {
    concernText: 'signs of pigmentation',
    reasonText: 'sun exposure, or photo-damage',
    expectedOutcome: 'restore your skin balance, revealing a brighter and more uniform appearance.',
  },

  // Face — combined concerns
  'COMEDONES,DARK_SPOTS_OR_MARK': {
    concernText: 'small bumps and dark spots',
    reasonText: 'excess oil, or past acne',
    expectedOutcome: 'clear pores, reduce bumps and fade dark spots for a more even skin tone.',
  },
  'UNDER_EYE_DARK_CIRCLES,COMEDONES': {
    concernText: 'dark circles and small bumps',
    reasonText: 'fatigue, clogged pores, or excess oil',
    expectedOutcome: 'brighten dark circles and clear comedones for clear, even-toned skin.',
  },
  'UNDER_EYE_DARK_CIRCLES,PIGMENTATION': {
    concernText: 'dark circles and pigmentation',
    reasonText: 'fatigue, or sun damage',
    expectedOutcome: 'help brighten your skin and even out your tone.',
  },
  'UNDER_EYE_DARK_CIRCLES,ANTI_AGING': {
    concernText: 'dark circles and fine-lines',
    reasonText: 'slow cell turnover, or collagen decline',
    expectedOutcome: 'brighten dark circles and support skin renewal for a fresh, youthful look.',
  },
  'DARK_SPOTS_OR_MARK,COMEDONES': {
    concernText: 'dark spots and small bumps',
    reasonText: 'excess oil, or past acne',
    expectedOutcome: 'clear pores, reduce bumps and fade dark spots for a more even skin tone.',
  },
  'PIGMENTATION,COMEDONES': {
    concernText: 'pigmentation and small bumps',
    reasonText: 'sun exposure, clogged pores, or dead skin',
    expectedOutcome: 'brighten your skin and clear comedones for an even-toned skin.',
  },

  // Hair — single concerns
  'DANDRUFF': {
    concernText: 'dandruff',
    reasonText: 'dryness, or fungal imbalance',
    expectedOutcome: 'sooth your scalp, reduce flakes and maintain long-term comfort.',
  },
  'FRIZZY_HAIR': {
    concernText: 'frizzy hair',
    reasonText: 'moisture loss, or cuticle damage',
    expectedOutcome: 'improve hydration, smooth the hair fibre and make your hair easier to manage.',
  },
  'GREY_HAIR': {
    concernText: 'signs of grey hair',
    reasonText: 'reduced melanin production, genetics, or nutrient gaps',
    expectedOutcome: 'support scalp nourishment and maintain your hair\'s natural appearance.',
  },
  'REGULAR_HAIR_CARE': {
    concernText: 'slight imbalance in hair health',
    reasonText: 'nutrient gaps, or product mismatch',
    expectedOutcome: 'help you improve overall hair strength, shine and manageability.',
  },
  'HAIR_FALL': {
    concernText: 'hair fall',
    reasonText: 'weakened roots, stress, nutrient gaps, or poor scalp health',
    expectedOutcome: 'strengthen your follicles and support healthier, fuller-looking hair.',
  },

  // Hair — combined concerns
  'DANDRUFF,HAIR_FALL': {
    concernText: 'dandruff and hair fall',
    reasonText: 'dryness, poor scalp health, or weakened roots',
    expectedOutcome: 'sooth your scalp, strengthen your follicles and support healthy, fuller-looking hair.',
  },
  'FRIZZY_HAIR,DANDRUFF': {
    concernText: 'frizzy hair and dandruff',
    reasonText: 'dryness, moisture loss, or cuticle damage',
    expectedOutcome: 'sooth your scalp and smoothen the hair fibre — making it healthy and easy to manage.',
  },
  'FRIZZY_HAIR,HAIR_FALL': {
    concernText: 'frizzy hair and hair fall',
    reasonText: 'dryness, or weakened roots',
    expectedOutcome: 'strengthen your follicles and smoothen the hair fibre — for fuller-looking, easy to manage hair.',
  },
  'GREY_HAIR,DANDRUFF': {
    concernText: 'greying and dandruff',
    reasonText: 'reduced melanin, genetics, or dryness',
    expectedOutcome: 'nourish the scalp, reduce flakes and maintain your hair\'s natural appearance.',
  },
  'GREY_HAIR,HAIR_FALL': {
    concernText: 'greying and hair fall',
    reasonText: 'genetics, nutrient gaps, or weakened roots',
    expectedOutcome: 'strengthen your follicles and maintain your hair\'s natural appearance.',
  },
  'DANDRUFF,HAIR_LOSS': {
    concernText: 'dandruff and hair fall',
    reasonText: 'dryness, poor scalp health, or weakened roots',
    expectedOutcome: 'sooth your scalp, strengthen your follicles and support healthy, fuller-looking hair.',
  },
  'FRIZZY_HAIR,HAIR_LOSS': {
    concernText: 'frizzy hair and hair fall',
    reasonText: 'dryness, or weakened roots',
    expectedOutcome: 'strengthen your follicles and smoothen the hair fibre — for fuller-looking, easy to manage hair.',
  },
  'GREY_HAIR,HAIR_LOSS': {
    concernText: 'greying and hair fall',
    reasonText: 'genetics, nutrient gaps, or weakened roots',
    expectedOutcome: 'strengthen your follicles and maintain your hair\'s natural appearance.',
  },
  'HAIR_LOSS': {
    concernText: 'hair fall',
    reasonText: 'weakened roots, stress, nutrient gaps, or poor scalp health',
    expectedOutcome: 'strengthen your follicles and support healthier, fuller-looking hair.',
  },
};

// Key format: '{age}_{hormonalImpact}'
export const AGE_HORMONAL_MAPPINGS: { [key: string]: string } = {
  '<17_PCOD': 'hormonal changes',
  '<17_REGULAR_PERIODS': 'hormonal changes',
  '<17_IRREGULAR_PERIODS': 'hormonal changes',
  '17-45_PCOD': 'hormonal imbalance',
  '17-45_IRREGULAR_PERIODS': 'hormonal imbalance',
  '17-45_REGULAR_PERIODS': '',
  '>45_PCOD': 'Ageing',
  '>45_REGULAR_PERIODS': 'Ageing',
  '>45_IRREGULAR_PERIODS': 'Ageing',
  '>30_PCOD': 'Ageing',
  '>30_REGULAR_PERIODS': 'Ageing',
  '>30_IRREGULAR_PERIODS': 'Ageing',
};

export const PREGNANCY_MAPPINGS: { [key: string]: string } = {
  'PREGNANT': 'hormonal changes during pregnancy',
  'NOT_PREGNANT': '',
  'BREASTFEEDING': 'hormonal changes during pregnancy',
};

export const STEROID_MAPPINGS: { [key: string]: string } = {
  'YES': 'steroid abuse',
  'NO': '',
};

export const SENSITIVE_SKIN_PREFIX: { [key: string]: string } = {
  'YES': 'Given your sensitive skin, a',
  'NO': 'A',
};

// ─── Hindi Mappings ───────────────────────────────────────────────────────────

export const CONCERN_MAPPINGS_HI: { [key: string]: ConcernMapping } = {
  // Face — single concerns
  'ACNE_OR_PIMPLES': {
    concernText: 'मुंहासों',
    reasonText: 'अतिरिक्त तेल, बंद रोमछिद्र या बैक्टीरिया का जमाव',
    expectedOutcome: 'मुंहासों को शांत करने और त्वचा को साफ बनाने में मदद कर सकती है।',
  },
  'ACNE_SCARS': {
    concernText: 'मुंहासों के दाग',
    reasonText: 'पहले के मुंहासों, या उन्हें दबाने और छेड़ने',
    expectedOutcome: 'त्वचा की बनावट में सुधार कर दाग कम करने में मदद कर सकती है।',
  },
  'ANTI_AGING': {
    concernText: 'झुर्रियां और कसाव की कमी',
    reasonText: 'धीमी सेल रिन्यूअल या कोलेजन की कमी',
    expectedOutcome: 'त्वचा की नवीनीकरण में सुधार कर युवा लुक बनाए रखने में मदद कर सकती है।',
  },
  'COMEDONES': {
    concernText: 'छोटे-छोटे दाने',
    reasonText: 'बंद रोमछिद्रों या अतिरिक्त तेल',
    expectedOutcome: 'दानों को कम करने और त्वचा की बनावट सुधारने में मदद कर सकती है।',
  },
  'UNDER_EYE_DARK_CIRCLES': {
    concernText: 'काले घेरे',
    reasonText: 'थकान या रक्त संचार कम होने',
    expectedOutcome: 'काले घेरों को हल्का कर त्वचा को अधिक ताज़ा और समान रंगत देने में मदद कर सकती है।',
  },
  'DARK_SPOTS_OR_MARK': {
    concernText: 'काले धब्बे',
    reasonText: 'मेलेनिन के अधिक उत्पादन, धूप या पुराने मुंहासों',
    expectedOutcome: 'धब्बों को हल्का और आगे दाग बनने से रोकने में मदद कर सकती है।',
  },
  'DULL_SKIN': {
    concernText: 'निखार की कमी',
    reasonText: 'डिहाइड्रेशन, धीमी सेल रिन्यूअल या मृत त्वचा कोशिकाओं के जमाव',
    expectedOutcome: 'एक्सफोलिएशन और हाइड्रेशन पर ध्यान देकर त्वचा में चमक और ताज़गी वापस ला सकती है।',
  },
  'PIGMENTATION': {
    concernText: 'झाइयों',
    reasonText: 'धूप या फोटो-डैमेज',
    expectedOutcome: 'इन भूरे पैच को हल्का करने और त्वचा की रंगत को समान बनाने में मदद कर सकती है।',
  },

  // Face — combined concerns
  'COMEDONES,DARK_SPOTS_OR_MARK': {
    concernText: 'छोटे दाने और काले धब्बे',
    reasonText: 'अतिरिक्त तेल या पुराने मुंहासों',
    expectedOutcome: 'दानों को कम करने और काले धब्बों को हल्का करने में मदद कर सकती है।',
  },
  'UNDER_EYE_DARK_CIRCLES,COMEDONES': {
    concernText: 'काले घेरे और छोटे दाने',
    reasonText: 'थकान, बंद रोमछिद्रों या अतिरिक्त तेल',
    expectedOutcome: 'काले घेरों को हल्का कर और दानों को कम कर त्वचा को समान रंगत देने में मदद कर सकती है।',
  },
  'UNDER_EYE_DARK_CIRCLES,PIGMENTATION': {
    concernText: 'झाइयां और काले घेरे',
    reasonText: 'थकान या धूप से हुए नुकसान',
    expectedOutcome: 'त्वचा को निखारने और रंगत को समान बनाने में मदद कर सकती है।',
  },
  'UNDER_EYE_DARK_CIRCLES,ANTI_AGING': {
    concernText: 'महीन रेखाएं और काले घेरे',
    reasonText: 'धीमी सेल रिन्यूअल या कोलेजन की कमी',
    expectedOutcome: 'काले घेरों को हल्का और त्वचा के नवीनीकरण कर युवा लुक देने में मदद कर सकती है।',
  },
  'DARK_SPOTS_OR_MARK,COMEDONES': {
    concernText: 'काले धब्बे और छोटे दाने',
    reasonText: 'अतिरिक्त तेल और पुराने मुंहासों',
    expectedOutcome: 'रोमछिद्रों को साफ कर दानों को कम और धब्बों को हल्का करने में मदद कर सकती है।',
  },
  'PIGMENTATION,COMEDONES': {
    concernText: 'झाइयां और छोटे दाने',
    reasonText: 'धूप, बंद रोमछिद्रों या मृत त्वचा के जमाव',
    expectedOutcome: 'त्वचा को निखारने और कॉमेडोन को साफ कर समान रंगत देने में मदद कर सकती है।',
  },

  // Hair — single concerns
  'DANDRUFF': {
    concernText: 'डैंड्रफ',
    reasonText: 'रूखापन या फंगल असंतुलन',
    expectedOutcome: 'स्कैल्प को शांत और फ्लेक्स को कम कर, लंबे समय तक आराम दे सकती है।',
  },
  'FRIZZY_HAIR': {
    concernText: 'फ्रिज़ी बालों',
    reasonText: 'नमी की कमी या क्यूटिकल डैमेज',
    expectedOutcome: 'हाइड्रेशन सुधारकर, हेयर फाइबर को स्मूद और बालों को आसानी से मैनेज करने में मदद कर सकती है।',
  },
  'GREY_HAIR': {
    concernText: 'बालों के सफेद होने',
    reasonText: 'जेनेटिक्स या मेलेनिन और पोषण की कमी',
    expectedOutcome: 'स्कैल्प को पोषण देकर बालों की प्राकृतिक दिखावट बनाए रखने में मदद कर सकती है।',
  },
  'REGULAR_HAIR_CARE': {
    concernText: 'बालों की सेहत में हल्का असंतुलन',
    reasonText: 'पोषण की कमी या गलत प्रोडक्ट',
    expectedOutcome: 'बालों की मजबूती, चमक और मैनेजेबिलिटी सुधारने में मदद कर सकती है।',
  },
  'HAIR_FALL': {
    concernText: 'बाल झड़ने',
    reasonText: 'कमजोर जड़, तनाव, पोषण की कमी या खराब स्कैल्प हेल्थ',
    expectedOutcome: 'हेयर फॉलिकल्स को मजबूत कर बालों को अधिक स्वस्थ और घना दिखाने में मदद कर सकती है।',
  },

  // Hair — combined concerns
  'DANDRUFF,HAIR_FALL': {
    concernText: 'डैंड्रफ और बाल झड़ने',
    reasonText: 'रूखापन, खराब स्कैल्प हेल्थ या कमजोर जड़',
    expectedOutcome: 'स्कैल्प को शांत और हेयर फॉलिकल्स को मजबूत कर, बालों को घना व स्वस्थ बना सकती है।',
  },
  'FRIZZY_HAIR,DANDRUFF': {
    concernText: 'फ्रिज़ी बाल और डैंड्रफ',
    reasonText: 'रूखापन, नमी की कमी या क्यूटिकल डैमेज',
    expectedOutcome: 'स्कैल्प को शांत और हेयर फाइबर को स्मूद बनाकर, बालों को स्वस्थ और आसानी से मैनेज करने योग्य बना सकती है।',
  },
  'FRIZZY_HAIR,HAIR_FALL': {
    concernText: 'फ्रिज़ी बाल और और बाल झड़ने',
    reasonText: 'रूखापन या कमजोर जड़',
    expectedOutcome: 'हेयर फॉलिकल्स को मजबूत और फाइबर को स्मूद कर बालों को घना और आसानी से मैनेज करने योग्य बना सकती है।',
  },
  'GREY_HAIR,DANDRUFF': {
    concernText: 'बालों के सफेद होने और डैंड्रफ',
    reasonText: 'जेनेटिक्स, रूखापन, या मेलेनिन की कमी',
    expectedOutcome: 'स्कैल्प को पोषण देकर, फ्लेक्स कम करने और बालों की प्राकृतिक दिखावट बनाए रखने में मदद कर सकती है।',
  },
  'GREY_HAIR,HAIR_FALL': {
    concernText: 'बालों के सफेद होने और बाल झड़ने',
    reasonText: 'जेनेटिक्स, पोषण की कमी या कमजोर जड़',
    expectedOutcome: 'हेयर फॉलिकल्स को मजबूत और बालों की प्राकृतिक दिखावट बनाए रखने में मदद कर सकती है।',
  },
  'DANDRUFF,HAIR_LOSS': {
    concernText: 'डैंड्रफ और बाल झड़ने',
    reasonText: 'रूखापन, खराब स्कैल्प हेल्थ या कमजोर जड़',
    expectedOutcome: 'स्कैल्प को शांत और हेयर फॉलिकल्स को मजबूत कर, बालों को घना व स्वस्थ बना सकती है।',
  },
  'FRIZZY_HAIR,HAIR_LOSS': {
    concernText: 'फ्रिज़ी बाल और और बाल झड़ने',
    reasonText: 'रूखापन या कमजोर जड़',
    expectedOutcome: 'हेयर फॉलिकल्स को मजबूत और फाइबर को स्मूद कर बालों को घना और आसानी से मैनेज करने योग्य बना सकती है।',
  },
  'GREY_HAIR,HAIR_LOSS': {
    concernText: 'बालों के सफेद होने और बाल झड़ने',
    reasonText: 'जेनेटिक्स, पोषण की कमी या कमजोर जड़',
    expectedOutcome: 'हेयर फॉलिकल्स को मजबूत और बालों की प्राकृतिक दिखावट बनाए रखने में मदद कर सकती है।',
  },
  'HAIR_LOSS': {
    concernText: 'बाल झड़ने',
    reasonText: 'कमजोर जड़, तनाव, पोषण की कमी या खराब स्कैल्प हेल्थ',
    expectedOutcome: 'हेयर फॉलिकल्स को मजबूत कर बालों को अधिक स्वस्थ और घना दिखाने में मदद कर सकती है।',
  },
};

// Key format: '{age}_{hormonalImpact}'
export const AGE_HORMONAL_MAPPINGS_HI: { [key: string]: string } = {
  '<17_PCOD': 'हार्मोन में परिवर्तन, ',
  '<17_REGULAR_PERIODS': 'हार्मोन में परिवर्तन, ',
  '<17_IRREGULAR_PERIODS': 'हार्मोन में परिवर्तन, ',
  '17-45_PCOD': 'हार्मोन का असंतुलन, ',
  '17-45_IRREGULAR_PERIODS': 'हार्मोन का असंतुलन, ',
  '17-45_REGULAR_PERIODS': '',
  '>45_PCOD': 'बढ़ती उम्र, ',
  '>45_REGULAR_PERIODS': 'बढ़ती उम्र, ',
  '>45_IRREGULAR_PERIODS': 'बढ़ती उम्र, ',
  '>30_PCOD': 'बढ़ती उम्र, ',
  '>30_REGULAR_PERIODS': 'बढ़ती उम्र, ',
  '>30_IRREGULAR_PERIODS': 'बढ़ती उम्र, ',
};

export const PREGNANCY_MAPPINGS_HI: { [key: string]: string } = {
  'PREGNANT': 'गर्भावस्था के दौरान हार्मोन में परिवर्तन',
  'NOT_PREGNANT': '',
  'BREASTFEEDING': 'गर्भावस्था के दौरान हार्मोन में परिवर्तन',
};

export const STEROID_MAPPINGS_HI: { [key: string]: string } = {
  'YES': 'स्टेरॉयड का दुरुपयोग',
  'NO': '',
};

export const SENSITIVE_SKIN_SUFFIX_HI: { [key: string]: string } = {
  'YES': ' हो सकते हैं। आपकी संवेदनशील त्वचा को देखते हुए, एक',
  'NO': ' हो सकते हैं।',
};
